import React, { useState } from 'react';
import { MapPinIcon, GlobeAltIcon, ClipboardIcon, MapIcon } from '@heroicons/react/24/outline';
import { CheckIcon } from '@heroicons/react/24/solid';
import Input from './Input';
import Button from './Button';
import MapPicker from './MapPicker';

interface LocationInputProps {
  coordinates?: string;
  mapUrl?: string;
  onCoordinatesChange: (coordinates: string) => void;
  onMapUrlChange: (url: string) => void;
  disabled?: boolean;
}

const LocationInput: React.FC<LocationInputProps> = ({
  coordinates = '',
  mapUrl = '',
  onCoordinatesChange,
  onMapUrlChange,
  disabled = false
}) => {
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [locationError, setLocationError] = useState('');
  const [showMapPicker, setShowMapPicker] = useState(false);

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by your browser');
      return;
    }

    setIsGettingLocation(true);
    setLocationError('');

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        const coordString = `${latitude},${longitude}`;
        onCoordinatesChange(coordString);
        
        // Generate Google Maps URL
        const googleMapsUrl = `https://maps.google.com/?q=${latitude},${longitude}`;
        onMapUrlChange(googleMapsUrl);
        
        setIsGettingLocation(false);
      },
      (error) => {
        setLocationError(`Error getting location: ${error.message}`);
        setIsGettingLocation(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  const openInGoogleMaps = () => {
    if (mapUrl) {
      window.open(mapUrl, '_blank');
    } else if (coordinates) {
      const [lat, lng] = coordinates.split(',');
      window.open(`https://maps.google.com/?q=${lat},${lng}`, '_blank');
    }
  };

  const copyToClipboard = (text: string, field: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    });
  };

  const validateCoordinates = (value: string) => {
    if (!value) return true;
    const pattern = /^-?\d+\.?\d*,-?\d+\.?\d*$/;
    return pattern.test(value);
  };

  const handleCoordinatesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    onCoordinatesChange(value);
    
    // Auto-generate Google Maps URL if valid coordinates
    if (validateCoordinates(value) && value) {
      const [lat, lng] = value.split(',');
      const googleMapsUrl = `https://maps.google.com/?q=${lat},${lng}`;
      onMapUrlChange(googleMapsUrl);
    }
  };

  const handleLocationFromMap = (coords: string, url: string) => {
    onCoordinatesChange(coords);
    onMapUrlChange(url);
    setShowMapPicker(false);
  };

  return (
    <div className="space-y-4">
      {/* Map Picker Modal */}
      <MapPicker
        isOpen={showMapPicker}
        onClose={() => setShowMapPicker(false)}
        onSelectLocation={handleLocationFromMap}
        initialCoordinates={coordinates}
      />
      
      {/* Location Coordinates */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          <MapPinIcon className="inline-block w-4 h-4 mr-1" />
          Location Coordinates
        </label>
        <div className="flex gap-2">
          <div className="flex-1">
            <Input
              type="text"
              placeholder="latitude,longitude (e.g., 10.7905,78.7047)"
              value={coordinates}
              onChange={handleCoordinatesChange}
              disabled={disabled}
            />
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setShowMapPicker(true)}
            disabled={disabled}
            className="px-3"
            title="Pick location on map"
          >
            <MapIcon className="w-4 h-4" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={getCurrentLocation}
            disabled={disabled || isGettingLocation}
            className="px-3"
            title="Get current location"
          >
            {isGettingLocation ? (
              <div className="w-4 h-4 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
            ) : (
              <MapPinIcon className="w-4 h-4" />
            )}
          </Button>
          {coordinates && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => copyToClipboard(coordinates, 'coordinates')}
              disabled={disabled}
              className="px-3"
            >
              {copiedField === 'coordinates' ? (
                <CheckIcon className="w-4 h-4 text-green-600" />
              ) : (
                <ClipboardIcon className="w-4 h-4" />
              )}
            </Button>
          )}
        </div>
        {locationError && (
          <p className="text-sm text-red-600 mt-1">{locationError}</p>
        )}
      </div>

      {/* Google Maps URL */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          <GlobeAltIcon className="inline-block w-4 h-4 mr-1" />
          Google Maps URL
        </label>
        <div className="flex gap-2">
          <div className="flex-1">
            <Input
              type="text"
              placeholder="https://maps.google.com/..."
              value={mapUrl}
              onChange={(e) => onMapUrlChange(e.target.value)}
              disabled={disabled}
            />
          </div>
          {(mapUrl || coordinates) && (
            <>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={openInGoogleMaps}
                disabled={disabled || (!mapUrl && !coordinates)}
                className="px-3"
              >
                <GlobeAltIcon className="w-4 h-4" />
              </Button>
              {mapUrl && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(mapUrl, 'url')}
                  disabled={disabled}
                  className="px-3"
                >
                  {copiedField === 'url' ? (
                    <CheckIcon className="w-4 h-4 text-green-600" />
                  ) : (
                    <ClipboardIcon className="w-4 h-4" />
                  )}
                </Button>
              )}
            </>
          )}
        </div>
        {mapUrl && (
          <div className="mt-2">
            <button
              type="button"
              className="text-sm text-blue-600 hover:text-blue-800 underline"
              onClick={openInGoogleMaps}
            >
              Open in Google Maps →
            </button>
          </div>
        )}
      </div>

      {/* Instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
        <p className="font-medium text-blue-900 mb-1">Location Tracking Options:</p>
        <ul className="text-blue-800 space-y-1 ml-4">
          <li>• 🗺️ Map icon - Open Google Maps to pick & mark location</li>
          <li>• 📍 Pin icon - Get current GPS location automatically</li>
          <li>• ✏️ Enter coordinates manually (latitude,longitude)</li>
          <li>• 📋 Paste a Google Maps URL directly</li>
          <li>• 🌐 Globe icon - View saved location in Google Maps</li>
        </ul>
      </div>
    </div>
  );
};

export default LocationInput;